/**
 * The Portal Page's data model — design_handoff_portal_profile_editor (Oct 2 2026), "State & data".
 *
 * One plain object feeds both screens: the public page renders it, the editor edits it and hands
 * the same object to the live preview. `decodePortalPage(profile)` builds it from a ProfileDto
 * and `buildPortalPayload(page, profile)` turns the edited one back into the PUT /profiles/me
 * body (the shape onboarding writes, plus the editor's own keys under extendedProfile:
 * `photoFocus`, `avatarFocus`, `avatarPhotoId`, `coverLayout`, `sections`).
 *
 * The chip vocabulary, the caps and the handle rule live here so the editor and Onboarding v2
 * (`/onboard`) pick from one list and refuse on one rule.
 */
import { clamp, desktopLayout, mobileLayout } from './portalCover';

export const GENDER_OPTIONS = ['MAN', 'WOMAN', 'NON-BINARY', 'PREFER NOT TO SAY'];
const GENDER_LABEL = { MAN: 'Man', WOMAN: 'Woman', 'NON-BINARY': 'Non-binary', 'PREFER NOT TO SAY': 'Prefer not to say' };
export const genderLabel = (g) => (g ? GENDER_LABEL[String(g).toUpperCase()] || g : '');
/** The gender as it reads on the page — the "prefer not to say" answer is itself private. */
export const genderOnPage = (g) => (g && String(g).toUpperCase() !== 'PREFER NOT TO SAY' ? genderLabel(g) : '');

export const STRENGTH_CHIPS = [
  'Storyteller', 'Mixology', 'Connector', 'Humor', 'Photography', 'Aesthetic Eye', 'Music Curation',
  'Performance Art', 'Grounding Presence', 'Mischief Maker', 'Cook',
];
export const COUNT_ON_ME_CHIPS = [
  'Spark Deep Conversation', 'Introduce People', 'Start A Dance Floor', 'Make A Round Of Drinks',
  'Bring A Great Bottle', 'Pick The Music', 'Cook Something', 'Hold Grounding Energy',
  'Document The Night', 'Hosting Backup', 'Cleanup Help',
];
export const DOWN_FOR_CHIPS = [
  'Dinner Parties', 'Game Nights', 'Live Music & Performance', 'Wine Tastings', 'Outdoor Adventures',
  'Playing Sports', 'Watching Sports', 'Workshops', 'Salon Conversations', 'Dance Floors & Nightlife',
  'Sensual & Intimate', 'Festivals',
];
export const SENSITIVE_CHIP = 'Sensual & Intimate';
export const MAX_STRENGTHS = 4;
export const MAX_PHOTOS = 9;
export const VIBE_MAX = 80;

/**
 * WHAT YOU DO and the two social handles (sign-up v3's YOU strip, Oct 22 2026; edited in THE
 * BASICS). Instagram is the profile's own `socialLink` column (stored as a URL, shown as a
 * handle); the occupation and TikTok live on `extendedProfile` as `occupation` / `tiktok`, bare
 * strings, the same way `hereToFind` does — so no column was added. All three are public.
 */
export const OCCUPATION_MAX = 40;
export const SOCIAL_HANDLE_MAX = 30;

/** `@x`, ` x `, `https://www.tiktok.com/@x?lang=en` → `x`; never longer than `SOCIAL_HANDLE_MAX`. */
export function socialHandle(raw) {
  if (typeof raw !== 'string') return '';
  return raw
    .trim()
    .replace(/^https?:\/\/(www\.)?[a-z0-9.-]+\//i, '')
    .replace(/^@/, '')
    .replace(/[/?#].*$/, '')
    .replace(/\s/g, '')
    .slice(0, SOCIAL_HANDLE_MAX);
}

/**
 * The three chip groups as one list: the key on the page object, the title, the helper, the
 * vocabulary, the cap and the selected-chip colours. Both the Portal Editor's STRENGTHS + TAGS tab
 * and Onboarding v2's step 3 map over this, so the copy, the caps and the colours cannot drift.
 *
 * COUNT ON ME FOR has no cap (the old intake capped it at 4; design_handoff_onboarding_v2 settled
 * on the editor's "no cap" — it is a list of offers, and a longer one is a better one).
 */
export const CHIP_GROUPS = [
  {
    key: 'strengths', title: 'STRENGTHS', chips: STRENGTH_CHIPS, max: MAX_STRENGTHS, bg: '#fff', fg: '#000',
    sub: 'The traits people remember after the night ends. Up to 4.',
  },
  {
    key: 'countOn', title: 'COUNT ON ME FOR', chips: COUNT_ON_ME_CHIPS, max: 0, bg: '#FFCCE6', fg: '#000',
    sub: 'What you’ll actually do at a gathering. Only pick what you mean.',
  },
  {
    key: 'downFor', title: 'DOWN FOR', chips: DOWN_FOR_CHIPS, max: 0, bg: '#A8D8EA', fg: '#000',
    sub: 'The kinds of rooms you say yes to. As many as you want.',
  },
];

/**
 * HERE TO FIND (sign-up v3, Oct 2026) — what you came for; Portal uses it to introduce you.
 * Saved as `extendedProfile.hereToFind` with `lookingFor` (one line, ≤ `LOOKING_MAX`) beside it.
 * `LOVE_CHIP` is shown to nobody: the API strips it from every public read and it only matches
 * two people who both picked it.
 */
export const HERE_TO_FIND = [
  'Friends', 'New in town', 'People to go out with', 'Dinner people', 'Outdoors', 'Business',
  'Creative partners', 'Music', 'Love',
];
export const LOVE_CHIP = 'Love';
export const LOOKING_MAX = 90;
export const LOOKING_EXAMPLES = ['A DJ for Dec 12', 'A cofounder who can sell', 'Anyone who likes long dinners'];

export const VIBE_EXAMPLES = [
  'Quiet at first, dangerous after dinner.',
  "I'm the friend who knows when the music needs to get louder.",
  'Better at midnight than at 8pm.',
  'I bring snacks and questions.',
];

/** About sections, in their default order. `k` is what `extendedProfile.sections` stores. */
export const SECTION_META = {
  music: ['MUSIC THAT MOVES ME', 'Your three artists, numbered.'],
  strengths: ['STRENGTHS', 'Up to four traits.'],
  countOn: ['COUNT ON ME FOR', 'What you bring to the room.'],
  downFor: ['DOWN FOR', 'The kinds of rooms you say yes to.'],
  guestlist: ['ON THE GUESTLIST', 'Events you are going to. Also shows the GUESTLIST tab.'],
  clubs: ['CLUBS', 'Clubs you belong to. Also shows the CLUBS tab.'],
};
export const SECTION_KEYS = Object.keys(SECTION_META);
export const defaultSections = () => SECTION_KEYS.map((k) => ({ k, on: true }));

/** The id the public page gives an avatar that was uploaded on its own and never joined the gallery. */
export const LEGACY_AVATAR_ID = 'avatar';

export function decodeExtended(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function decodeStringList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((s) => typeof s === 'string');
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

export function normalizeInstagram(input) {
  const trimmed = (input || '').trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://instagram.com/${trimmed.replace(/^@/, '')}`;
}

/** `https://instagram.com/x`, `@x`, `x` → `x`. */
export function instagramHandle(socialLink) {
  if (!socialLink) return '';
  return socialLink
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
    .replace(/^@/, '')
    .replace(/[/?#].*$/, '')
    .trim();
}

/** "Feb 2026" — null for a missing or default (year 1) date. */
export function memberSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 2000) return null;
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
}

const pct = (v, fallback = 50) => (Number.isFinite(+v) ? clamp(Math.round(+v), 0, 100) : fallback);

/** Normalise a saved sections list: every known key once, in the saved order, unknown keys dropped. */
export function normalizeSections(raw) {
  const out = [];
  const seen = new Set();
  if (Array.isArray(raw)) {
    raw.forEach((s) => {
      const k = s && typeof s === 'object' ? s.k : null;
      if (!k || seen.has(k) || !SECTION_META[k]) return;
      seen.add(k);
      out.push({ k, on: s.on !== false });
    });
  }
  SECTION_KEYS.forEach((k) => { if (!seen.has(k)) out.push({ k, on: true }); });
  return out;
}

/**
 * The photos on the cover, in order: the gallery, with each photo's saved focal point. An avatar
 * that was uploaded separately (before the editor existed) and never joined the gallery leads as a
 * virtual photo — the old page did the same — unless `avatarPhotoId` says the avatar is a crop of
 * a gallery photo, in which case the crop is not a second picture.
 */
export function coverPhotos(profile, ext = decodeExtended(profile?.extendedProfile)) {
  const focus = ext.photoFocus && typeof ext.photoFocus === 'object' ? ext.photoFocus : {};
  const at = (id) => ({ fx: pct(focus[id]?.x), fy: pct(focus[id]?.y) });
  const gallery = (Array.isArray(profile?.galleryPhotos) ? profile.galleryPhotos : [])
    .filter((p) => p && p.url)
    .map((p) => ({ id: String(p.id), src: p.url, ...at(String(p.id)) }));
  const avatarUrl = profile?.avatarUrl || null;
  const avatarInGallery = !!avatarUrl && gallery.some((p) => p.src === avatarUrl);
  const cropOfGallery = !!ext.avatarPhotoId && gallery.some((p) => p.id === String(ext.avatarPhotoId));
  if (avatarUrl && !avatarInGallery && !cropOfGallery) {
    return [{ id: LEGACY_AVATAR_ID, src: avatarUrl, ...at(LEGACY_AVATAR_ID), legacy: true }, ...gallery];
  }
  return gallery;
}

/** Which cover photo the avatar is: the saved pick, else the one with the avatar's URL, else photo 1. */
export function avatarPhotoId(profile, photos, ext = decodeExtended(profile?.extendedProfile)) {
  if (ext.avatarPhotoId && photos.some((p) => p.id === String(ext.avatarPhotoId))) return String(ext.avatarPhotoId);
  const byUrl = profile?.avatarUrl ? photos.find((p) => p.src === profile.avatarUrl) : null;
  if (byUrl) return byUrl.id;
  return photos[0]?.id || null;
}

/**
 * ProfileDto → the page object. `viewer` is 'self' for the owner (the API gave the full profile)
 * or 'visitor' (the public DTO, already filtered server-side).
 */
export function decodePortalPage(profile) {
  const ext = decodeExtended(profile?.extendedProfile);
  const downFor = decodeStringList(profile?.gatheringTypes);
  const photos = coverPhotos(profile, ext);
  const avatar = avatarPhotoId(profile, photos, ext);
  const avatarPhoto = photos.find((p) => p.id === avatar);
  const af = ext.avatarFocus && typeof ext.avatarFocus === 'object' ? ext.avatarFocus : null;
  const music = Array.isArray(ext.musicArtists) ? ext.musicArtists.map((s) => (typeof s === 'string' ? s : '')) : [];
  while (music.length < 3) music.push('');
  const layout = ext.coverLayout && typeof ext.coverLayout === 'object' ? ext.coverLayout : {};
  return {
    name: profile?.displayName || '',
    handle: profile?.handle || '',
    location: profile?.location || '',
    gender: profile?.gender || '',
    genderHidden: ext.genderHidden === true,
    instagram: instagramHandle(profile?.socialLink),
    occupation: typeof ext.occupation === 'string' ? ext.occupation.trim().slice(0, OCCUPATION_MAX) : '',
    tiktok: socialHandle(ext.tiktok),
    since: memberSince(profile?.createdAt),
    vibe: (profile?.vibeCheck || '').replace(/^["“]|["”]$/g, ''),
    music: music.slice(0, 3),
    anthem: ext.anthem && ext.anthem.trackId ? ext.anthem : null,
    strengths: Array.isArray(ext.strengths) ? ext.strengths.filter((s) => typeof s === 'string') : [],
    countOn: Array.isArray(ext.countOnMeFor) ? ext.countOnMeFor.filter((s) => typeof s === 'string') : [],
    downFor,
    // HedonisticVisible defaults to true server-side; the intake only lowers it when the chip is picked.
    sensualVisible: profile?.hedonisticVisible !== false,
    photos,
    avatar,
    ax: pct(af?.x, avatarPhoto?.fx ?? 50),
    ay: pct(af?.y, avatarPhoto?.fy ?? 50),
    layoutD: desktopLayout(layout.desktop),
    layoutM: mobileLayout(layout.mobile),
    sections: normalizeSections(ext.sections),
  };
}

/**
 * The PUT /profiles/me body for an edited page. Everything the intake wrote is written the same
 * way; the new keys ride along in extendedProfile (the avatar's two are the API's, see below).
 * `intakeCurrentStep` is deliberately absent — the editor never touches onboarding state.
 */
export function buildPortalPayload(page, profile) {
  const ext = {
    ...decodeExtended(profile?.extendedProfile),
    genderHidden: !!page.genderHidden,
    occupation: (page.occupation || '').trim().slice(0, OCCUPATION_MAX) || null,
    tiktok: socialHandle(page.tiktok) || null,
    musicArtists: page.music.map((s) => (s || '').trim()),
    anthem: page.anthem && page.anthem.trackId ? page.anthem : null,
    strengths: page.strengths,
    countOnMeFor: page.countOn,
    photoFocus: Object.fromEntries(page.photos.map((p) => [p.id, { x: pct(p.fx), y: pct(p.fy) }])),
    // avatarPhotoId / avatarFocus are NOT written here: POST avatar/from-gallery stamps them when
    // it crops the file, so the two never disagree with the avatar that is actually served.
    coverLayout: { desktop: desktopLayout(page.layoutD), mobile: mobileLayout(page.layoutM) },
    sections: page.sections.map(({ k, on }) => ({ k, on: on !== false })),
  };
  const body = {
    displayName: (page.name || '').trim() || undefined,
    location: (page.location || '').trim() || null,
    gender: page.genderHidden ? null : (page.gender || null),
    socialLink: normalizeInstagram(page.instagram),
    vibeCheck: (page.vibe || '').trim() || null,
    gatheringTypes: JSON.stringify(page.downFor),
    extendedProfile: JSON.stringify(ext),
    // Sensual & Intimate privacy: with the chip unpicked the toggle is moot and the platform
    // default (visible) stands, so other kinds picked later aren't hidden by accident.
    hedonisticVisible: page.downFor.includes(SENSITIVE_CHIP) ? !!page.sensualVisible : true,
  };
  const handle = (page.handle || '').trim().toLowerCase();
  if (handle && handle !== (profile?.handle || '').toLowerCase()) body.handle = handle;
  return body;
}

/** The handle as typed → what the API accepts: lowercase letters, digits, underscore. */
export const cleanHandle = (v) => (v || '').toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 50);

/**
 * Is this handle usable? The ONE handle rule — the Portal Editor's SAVE and Onboarding v2's
 * step 1 both ask this, so "taken", "reserved" and "too short" can only be decided here.
 *
 * `status` is the live `checkHandle` result (`{ checking, available, message }`). The refusal
 * wording is the server's, passed through verbatim: it distinguishes reserved from taken and we
 * are not in a position to guess which.
 *
 * @returns {{ kind: 'empty'|'short'|'checking'|'taken', message: string }|null} null when it is good.
 */
export function handleIssue(handle, status) {
  const h = (handle || '').trim();
  if (!h) return { kind: 'empty', message: 'A handle is required — it is your page link.' };
  if (h.length < 3) return { kind: 'short', message: 'At least 3 characters.' };
  if (status?.available === false) return { kind: 'taken', message: status.message || 'That handle is taken.' };
  if (status?.checking) return { kind: 'checking', message: 'Checking…' };
  return null;
}
